import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaFire, FaChartLine, FaCoins, FaGift, FaBell } from 'react-icons/fa';
import { useToast } from '../hooks/useToast';
import toastNotifications from '../utils/toastNotifications';
import Portal from './Portal';

const NotificationSettings = ({ isOpen, onClose }) => {
  const { disableAll, disableType } = useToast();
  const [preferences, setPreferences] = useState({
    enabled: true,
    enabledTypes: []
  });

  // Load current preferences
  useEffect(() => {
    try {
      const saved = localStorage.getItem('mainbet_toast_preferences');
      if (saved) {
        const parsed = JSON.parse(saved);
        setPreferences(parsed);
      } else {
        // Default preferences
        const defaultPrefs = {
          enabled: true,
          enabledTypes: ['top_match', 'odds_change', 'accumulator_prompt', 'new_bonus']
        };
        setPreferences(defaultPrefs);
      }
    } catch (error) {
      console.warn('Failed to load notification preferences:', error);
    }
  }, [isOpen]);

  // Save preferences to localStorage and sync with in-memory toastSystem
  const savePreferences = (newPrefs) => {
    try {
      localStorage.setItem('mainbet_toast_preferences', JSON.stringify(newPrefs));
      setPreferences(newPrefs);
      toastNotifications.reloadPreferences();
    } catch (error) {
      console.warn('Failed to save notification preferences:', error);
    }
  };

  // Handle master toggle
  const handleMasterToggle = (enabled) => {
    const newPrefs = { ...preferences, enabled };
    savePreferences(newPrefs);
    
    if (!enabled) {
      disableAll();
    }
  };

  // Handle individual type toggle
  const handleTypeToggle = (typeId, enabled) => {
    let newEnabledTypes;
    
    if (enabled) {
      newEnabledTypes = [...preferences.enabledTypes, typeId];
    } else {
      newEnabledTypes = preferences.enabledTypes.filter(id => id !== typeId);
      disableType(typeId);
    }
    
    const newPrefs = { ...preferences, enabledTypes: newEnabledTypes };
    savePreferences(newPrefs);
  };

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const notificationTypes = [
    {
      id: 'top_match',
      title: 'Spotlight match',
      description: 'One highlighted pick each matchday',
      icon: FaFire,
      example: 'Liverpool vs Manchester City: see our pick'
    },
    {
      id: 'odds_change',
      title: 'Odds updates',
      description: 'When odds move on matches you follow',
      icon: FaChartLine,
      example: 'Liverpool odds changed from 1.85 to 2.10'
    },
    {
      id: 'accumulator_prompt',
      title: 'Acca suggestions',
      description: "Today's ready-made accas",
      icon: FaCoins,
      example: 'Ready-made slips built from our picks'
    },
    {
      id: 'new_bonus',
      title: 'Welcome offers',
      description: 'New bookmaker promotions',
      icon: FaGift,
      example: '100% welcome bonus. See terms'
    }
  ];

  return (
    <Portal>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md max-h-[min(90dvh,calc(100%-2rem))] overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Notification Settings
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Customize your alerts
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Close"
              >
                <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {/* Master Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl mb-6">
                <div className="flex items-center space-x-3">
                  <FaBell className="w-5 h-5 text-primary flex-shrink-0" aria-hidden="true" />
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white">
                      All Notifications
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Master control for all alerts
                    </p>
                  </div>
                </div>
                <Toggle
                  enabled={preferences.enabled}
                  onChange={handleMasterToggle}
                  size="large"
                />
              </div>

              {/* Individual Type Settings */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Alert Types
                </h4>
                
                {notificationTypes.map((type) => (
                  <div key={type.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <type.icon className="w-5 h-5 text-primary flex-shrink-0" aria-hidden="true" />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                            {type.title}
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {type.description}
                          </p>
                        </div>
                      </div>
                      <Toggle
                        enabled={preferences.enabled && preferences.enabledTypes.includes(type.id)}
                        onChange={(enabled) => handleTypeToggle(type.id, enabled)}
                        disabled={!preferences.enabled}
                      />
                    </div>
                    
                    {/* Example */}
                    <p className="ml-8 text-xs text-gray-500 dark:text-gray-400">
                      Example: {type.example}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Changes are saved automatically
                </p>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>
    </Portal>
  );
};

// Toggle Component
const Toggle = ({ enabled, onChange, disabled = false, size = 'normal' }) => {
  const sizeClasses = {
    normal: 'w-11 h-6',
    large: 'w-12 h-7'
  };

  const dotSizeClasses = {
    normal: 'w-4 h-4',
    large: 'w-5 h-5'
  };

  return (
    <button
      onClick={() => !disabled && onChange(!enabled)}
      className={`
        ${sizeClasses[size]} rounded-full p-1 transition-colors duration-200 ease-in-out
        ${disabled 
          ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed' 
          : enabled 
            ? 'bg-blue-600 hover:bg-blue-700' 
            : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
        }
      `}
      disabled={disabled}
      aria-label={`Toggle ${enabled ? 'off' : 'on'}`}
    >
      <motion.div
        animate={{
          x: enabled ? (size === 'large' ? 20 : 16) : 0,
        }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        className={`
          ${dotSizeClasses[size]} bg-white rounded-full shadow-sm
          ${disabled ? 'opacity-70' : ''}
        `}
      />
    </button>
  );
};

export default NotificationSettings; 